import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createDualAxisGroupedConfig } from './chartConfigUtils';

interface CustomerSegmentData {
    readonly segment: string;
    readonly customerCount: number;
    readonly totalContracts: number;
    readonly totalValueRon: number;
    readonly avgContractValueRon: number;
    readonly redemptionRate: number;
}

interface CustomerSegmentChartProps {
    readonly data: readonly CustomerSegmentData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function CustomerSegmentChart({
    data,
    loading = false,
    currency = 'EUR',
}: CustomerSegmentChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const segmentTranslations: Record<string, string> = {
        VIP: t('statistics.bi.segments.vip', 'VIP'),
        Gold: t('statistics.bi.segments.gold', 'Gold'),
        Silver: t('statistics.bi.segments.silver', 'Silver'),
        Standard: t('statistics.bi.segments.standard', 'Standard'),
    };

    const currSuffix = currency === 'EUR' ? 'EUR' : 'RON';
    const categories = data.map(d => segmentTranslations[d.segment] ?? d.segment);
    const base = createDualAxisGroupedConfig(
        categories,
        t('statistics.bi.customerSegment', 'Customer Segment'),
        t('statistics.bi.count', 'Count'),
        `${t('statistics.bi.value', 'Value')} (${currSuffix})`,
    );

    const options: Highcharts.Options = {
        ...base,
        series: [
            {
                type: 'column',
                name: t('statistics.bi.customers', 'Customers'),
                data: data.map(d => d.customerCount),
                color: '#1890ff',
                yAxis: 0,
            },
            {
                type: 'column',
                name: t('statistics.bi.contracts', 'Contracts'),
                data: data.map(d => d.totalContracts),
                color: '#52c41a',
                yAxis: 0,
            },
            {
                type: 'spline',
                name: `${t('statistics.bi.totalValue', 'Total Value')} (${currSuffix})`,
                data: data.map(d => d.totalValueRon),
                color: '#fa541c',
                yAxis: 1,
                marker: {
                    enabled: true,
                },
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default CustomerSegmentChart;
